import { BISTPrice, CurrencyPrice, GoldPrice } from './types';

export class CanliDovizService {
  private readonly baseUrl = 'https://borsa.doviz.com/hisseler';

  async fetchBISTData(): Promise<BISTPrice[]> {
    try {
      const response = await fetch('/api/midas', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Eğer boş gelirse mock veri kullan
      if (!data || data.length === 0) {
        return this.getMockBISTData();
      }
      
      // JSON verisini BISTPrice formatına dönüştür
      const stocks = data.map((item: any) => ({
        StockCode: item.StockCode || item.code || 'UNKNOWN',
        Name: item.Name || item.name || 'Bilinmeyen',
        LastTransactionPrice: item.LastTransactionPrice || item.price || 0,
        Change: item.Change || item.change || 0,
        ChangePercent: item.ChangePercent || item.changePercent || 0,
        VolumeTL: item.VolumeTL || item.volume || 'N/A',
        VolumeLot: item.VolumeLot || item.lot || 'N/A',
        PriceDate: item.PriceDate || new Date().toISOString()
      }));
      
      return stocks;
    } catch (error) {
      console.error('❌ Error fetching from proxy:', error);
      // Hata olursa mock veri döndür
      return this.getMockBISTData();
    }
  }

  private getMockBISTData(): BISTPrice[] {
    const now = new Date();
    
    // Gerçekçi BIST verileri (güncel piyasa verilerine yakın)
    const baseData = [
      { 
        StockCode: 'THYAO', 
        LastPrice: 289.75, 
        PreviousClose: 288.00,
        VolumeTL: 234567890,
        VolumeLot: 822500,
        Name: 'Türk Hava Yolları AO'
      },
      { 
        StockCode: 'GARAN', 
        LastPrice: 46.12, 
        PreviousClose: 45.75,
        VolumeTL: 156789000,
        VolumeLot: 3420000,
        Name: 'Garanti Bankası A.Ş.'
      },
      { 
        StockCode: 'AKBNK', 
        LastPrice: 52.68, 
        PreviousClose: 52.30,
        VolumeTL: 289456000,
        VolumeLot: 5518000,
        Name: 'Akbank T.A.Ş.'
      },
      { 
        StockCode: 'SAHOL', 
        LastPrice: 39.45, 
        PreviousClose: 38.90,
        VolumeTL: 198765000,
        VolumeLot: 5084000,
        Name: 'Hacı Ömer Sabancı Holding A.Ş.'
      },
      { 
        StockCode: 'KCHOL', 
        LastPrice: 127.20, 
        PreviousClose: 125.60,
        VolumeTL: 456789000,
        VolumeLot: 3615000,
        Name: 'Koç Holding A.Ş.'
      },
      { 
        StockCode: 'BIMAS', 
        LastPrice: 662.50, 
        PreviousClose: 656.00,
        VolumeTL: 567890000,
        VolumeLot: 862000,
        Name: 'BİM Birleşik Mağazalar A.Ş.'
      },
      { 
        StockCode: 'ASELS', 
        LastPrice: 80.15, 
        PreviousClose: 78.90,
        VolumeTL: 123456000,
        VolumeLot: 1558000,
        Name: 'Aselsan Elektronik Sanayi ve Ticaret A.Ş.'
      },
      { 
        StockCode: 'TCELL', 
        LastPrice: 42.80, 
        PreviousClose: 42.15,
        VolumeTL: 98765000,
        VolumeLot: 2333000,
        Name: 'Türk Telekomünikasyon A.Ş.'
      },
      { 
        StockCode: 'SASA', 
        LastPrice: 46.25, 
        PreviousClose: 45.80,
        VolumeTL: 167890000,
        VolumeLot: 3652000,
        Name: 'Sasa Polyester Sanayi A.Ş.'
      },
      { 
        StockCode: 'EREGL', 
        LastPrice: 18.75, 
        PreviousClose: 18.45,
        VolumeTL: 234567000,
        VolumeLot: 12620000,
        Name: 'Ereğli Demir ve Çelik Fabrikaları T.A.Ş.'
      },
      { 
        StockCode: 'TOASO', 
        LastPrice: 288.10, 
        PreviousClose: 285.60,
        VolumeTL: 178901000,
        VolumeLot: 624000,
        Name: 'Tofaş Türk Otomobil Fabrikası A.Ş.'
      },
      { 
        StockCode: 'FROTO', 
        LastPrice: 590.50, 
        PreviousClose: 585.60,
        VolumeTL: 298765000,
        VolumeLot: 509000,
        Name: 'Ford Otomotiv Sanayi A.Ş.'
      },
      { 
        StockCode: 'SISE', 
        LastPrice: 86.20, 
        PreviousClose: 85.40,
        VolumeTL: 145678000,
        VolumeLot: 1696000,
        Name: 'Şişecam Cam Sanayi ve Ticaret A.Ş.'
      },
      { 
        StockCode: 'PETKM', 
        LastPrice: 45.60, 
        PreviousClose: 45.20,
        VolumeTL: 87654000,
        VolumeLot: 1934000,
        Name: 'Petkim Petrokimya Holding A.Ş.'
      },
      { 
        StockCode: 'ARCLK', 
        LastPrice: 28.75, 
        PreviousClose: 28.45,
        VolumeTL: 65432000,
        VolumeLot: 2289000,
        Name: 'Arçelik A.Ş.'
      },
      { 
        StockCode: 'MGROS', 
        LastPrice: 187.50, 
        PreviousClose: 185.50,
        VolumeTL: 198765000,
        VolumeLot: 1067000,
        Name: 'Migros Ticaret A.Ş.'
      },
      { 
        StockCode: 'HALKB', 
        LastPrice: 85.80, 
        PreviousClose: 85.20,
        VolumeTL: 109876000,
        VolumeLot: 1286000,
        Name: 'Halkbank A.Ş.'
      },
      { 
        StockCode: 'ISCTR', 
        LastPrice: 16.10, 
        PreviousClose: 15.85,
        VolumeTL: 456789000,
        VolumeLot: 28650000,
        Name: 'İş Bankası A.Ş.'
      },
      { 
        StockCode: 'VAKBN', 
        LastPrice: 28.80, 
        PreviousClose: 28.45,
        VolumeTL: 134567000,
        VolumeLot: 4710000,
        Name: 'VakıfBank T.A.Ş.'
      },
      { 
        StockCode: 'YKBNK', 
        LastPrice: 4.95, 
        PreviousClose: 4.85,
        VolumeTL: 87654000,
        VolumeLot: 17970000,
        Name: 'Yapı ve Kredi Bankası A.Ş.'
      },
      { 
        StockCode: 'TUPRS', 
        LastPrice: 100.50, 
        PreviousClose: 100.00,
        VolumeTL: 267890000,
        VolumeLot: 2686000,
        Name: 'Tüpraş-Türkiye Petrolleri A.Ş.'
      },
      { 
        StockCode: 'EKGY', 
        LastPrice: 79.20, 
        PreviousClose: 77.60,
        VolumeTL: 76543000,
        VolumeLot: 975000,
        Name: 'Ege Giyim Sanayi ve Ticaret A.Ş.'
      },
      { 
        StockCode: 'XU100', 
        LastPrice: 8025.50, 
        PreviousClose: 7950.25,
        VolumeTL: 0,
        VolumeLot: 0,
        Name: 'BIST 100 Endeksi'
      },
      { 
        StockCode: 'XU030', 
        LastPrice: 8310.25, 
        PreviousClose: 8235.80,
        VolumeTL: 0,
        VolumeLot: 0,
        Name: 'BIST 30 Endeksi'
      }
    ];

    // Hesaplamaları yaparak BISTPrice formatına dönüştür
    return baseData.map(item => {
      const change = item.LastPrice - item.PreviousClose;
      const changePercent = (change / item.PreviousClose) * 100;

      return {
        StockCode: item.StockCode,
        LastTransactionPrice: item.LastPrice,
        PriceDate: now.toISOString(),
        Change: change,
        ChangePercent: changePercent,
        VolumeTL: item.VolumeTL,
        VolumeLot: item.VolumeLot
      };
    });
  }

  // Canlı Döviz'den döviz verilerini çek
  async fetchCurrencyDataFromCanliDoviz(): Promise<CurrencyPrice[]> {
    try {
      const response = await fetch('/api/currency-rates', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Eğer boş gelirse mock veri kullan
      if (!data || data.length === 0) {
        return this.getMockCurrencyData();
      }
      
      // JSON verisini CurrencyPrice formatına dönüştür
      const currencies = data.map((item: any) => ({
        Code: item.code || item.Code || 'UNKNOWN',
        Name: item.name || item.Name || 'Bilinmeyen',
        BuyPrice: item.buying || item.BuyPrice || 0,
        SellPrice: item.selling || item.SellPrice || 0,
        Change: item.change || item.Change || 0,
        ChangePercent: item.changePercent || item.ChangePercent || 0
      }));
      
      return currencies;
    } catch (error) {
      console.error('❌ Error fetching from proxy:', error);
      // Hata olursa mock veri döndür
      return this.getMockCurrencyData();
    }
  }

  // Canlı Döviz'den altın verilerini çek
  async fetchGoldDataFromCanliDoviz(): Promise<GoldPrice[]> {
    try {
      const response = await fetch('/api/gold-prices', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Eğer boş gelirse mock veri kullan
      if (!data || data.length === 0) {
        return this.getMockGoldData();
      }
      
      // JSON verisini GoldPrice formatına dönüştür
      const golds = data.map((item: any) => ({
        Type: item.name || item.Type || 'Bilinmeyen',
        Price: item.price || item.Price || 0,
        Unit: item.Unit || 'TL',
        Change: item.change || item.Change || 0,
        ChangePercent: item.changePercent || item.ChangePercent || 0
      }));
      
      return golds;
    } catch (error) {
      console.error('❌ Error fetching from proxy:', error);
      // Hata olursa mock veri döndür
      return this.getMockGoldData();
    }
  }

  private getMockCurrencyData(): CurrencyPrice[] {
    return [
      { Code: 'USD', Name: 'Amerikan Doları', BuyPrice: 32.15, SellPrice: 32.25, Change: 0.05, ChangePercent: 0.15 },
      { Code: 'EUR', Name: 'Euro', BuyPrice: 34.85, SellPrice: 34.95, Change: -0.12, ChangePercent: -0.34 },
      { Code: 'GBP', Name: 'İngiliz Sterlini', BuyPrice: 40.25, SellPrice: 40.45, Change: 0.18, ChangePercent: 0.45 },
      { Code: 'CHF', Name: 'İsviçre Frangı', BuyPrice: 36.85, SellPrice: 37.05, Change: 0.08, ChangePercent: 0.22 },
      { Code: 'JPY', Name: 'Japon Yeni', BuyPrice: 0.215, SellPrice: 0.218, Change: -0.002, ChangePercent: -0.92 },
      { Code: 'SAR', Name: 'Suudi Arabistan Riyali', BuyPrice: 8.55, SellPrice: 8.65, Change: 0.02, ChangePercent: 0.23 },
      { Code: 'RUB', Name: 'Rus Rublesi', BuyPrice: 0.355, SellPrice: 0.365, Change: -0.005, ChangePercent: -1.38 },
      { Code: 'CNY', Name: 'Çin Yuanı', BuyPrice: 4.45, SellPrice: 4.55, Change: 0.01, ChangePercent: 0.22 }
    ];
  }

  private getMockGoldData(): GoldPrice[] {
    return [
      { Type: 'Çeyrek Altın', Price: 2850.50, Unit: 'Adet', Change: 15.20, ChangePercent: 0.54 },
      { Type: 'Yarım Altın', Price: 5701.00, Unit: 'Adet', Change: 30.40, ChangePercent: 0.54 },
      { Type: 'Tam Altın', Price: 11402.00, Unit: 'Adet', Change: 60.80, ChangePercent: 0.54 },
      { Type: 'Gram Altın', Price: 1850.25, Unit: 'Gram', Change: 9.85, ChangePercent: 0.53 },
      { Type: '14 Ayar Altın', Price: 1150.75, Unit: 'Gram', Change: 6.12, ChangePercent: 0.53 },
      { Type: '18 Ayar Altın', Price: 1480.20, Unit: 'Gram', Change: 7.89, ChangePercent: 0.53 },
      { Type: '22 Ayar Bilezik', Price: 1705.30, Unit: 'Gram', Change: 9.07, ChangePercent: 0.53 },
      { Type: 'Gümüş', Price: 23.85, Unit: 'Gram', Change: 0.12, ChangePercent: 0.51 },
      { Type: 'Cumhuriyet Altını', Price: 11402.00, Unit: 'Adet', Change: 60.80, ChangePercent: 0.54 },
      { Type: 'Ata Lira', Price: 11402.00, Unit: 'Adet', Change: 60.80, ChangePercent: 0.54 }
    ];
  }

  // Gerçek scraping metodu (backend'de kullanılabilir)
  private async scrapeCanliDovizData(): Promise<BISTPrice[]> {
    try {
      const response = await fetch(this.baseUrl);
      const html = await response.text();
      
      const stocks = this.parseCanliDovizHTML(html);
      return stocks;
    } catch (error) {
      console.error('Scraping error:', error);
      return this.getMockBISTData();
    }
  }

  private parseCanliDovizHTML(html: string): BISTPrice[] {
    const stocks: BISTPrice[] = [];
    const now = new Date();
    
    try {
      // Canlı Döviz BIST tablosunu parse et
      const stockRows = html.match(/<tr[^>]*class="[^"]*stock-row[^"]*"[^>]*>[\s\S]*?<\/tr>/g) || [];
      
      for (const row of stockRows) {
        try {
          // Sembolü bul
          const symbolMatch = row.match(/<td[^>]*class="[^"]*symbol[^"]*"[^>]*>([A-Z0-9]+)<\/td>/);
          // Fiyatı bul
          const priceMatch = row.match(/<td[^>]*class="[^"]*price[^"]*"[^>]*>([\d.,]+)<\/td>/);
          // Değişimi bul
          const changeMatch = row.match(/<td[^>]*class="[^"]*change[^"]*"[^>]*>([-+]?[\d.,]+)<\/td>/);
          // Yüzde değişimi bul
          const changePercentMatch = row.match(/<td[^>]*class="[^"]*percent[^"]*"[^>]*>([-+]?[\d.,]+)%<\/td>/);
          // Hacmi bul
          const volumeMatch = row.match(/<td[^>]*class="[^"]*volume[^"]*"[^>]*>([\d.,]+)<\/td>/);
          
          if (symbolMatch && priceMatch) {
            const symbol = symbolMatch[1];
            const price = parseFloat(priceMatch[1].replace(',', '.'));
            const change = changeMatch ? parseFloat(changeMatch[1].replace(',', '.')) : 0;
            const changePercent = changePercentMatch ? parseFloat(changePercentMatch[1].replace(',', '.')) : 0;
            const volume = volumeMatch ? parseFloat(volumeMatch[1].replace(',', '.')) : 0;
            
            stocks.push({
              StockCode: symbol,
              LastTransactionPrice: price,
              PriceDate: now.toISOString(),
              Change: change,
              ChangePercent: changePercent,
              VolumeTL: volume,
              VolumeLot: 0
            });
          }
        } catch (error) {
          console.error('Error parsing stock row:', error);
        }
      }
    } catch (error) {
      console.error('Error parsing BIST HTML:', error);
    }
    
    return stocks;
  }

  private parseCurrencyHTML(html: string): CurrencyPrice[] {
    const currencies: CurrencyPrice[] = [];
    
    try {
      // Canlı Döviz döviz tablosunu parse et
      const currencyRows = html.match(/<tr[^>]*class="[^"]*currency-row[^"]*"[^>]*>[\s\S]*?<\/tr>/g) || [];
      
      for (const row of currencyRows) {
        try {
          // Sembolü bul
          const symbolMatch = row.match(/<td[^>]*class="[^"]*symbol[^"]*"[^>]*>([A-Z]{3})<\/td>/);
          // İsmi bul
          const nameMatch = row.match(/<td[^>]*class="[^"]*name[^"]*"[^>]*>([^<]+)<\/td>/);
          // Alış fiyatını bul
          const buyMatch = row.match(/<td[^>]*class="[^"]*buying[^"]*"[^>]*>([\d.,]+)<\/td>/);
          // Satış fiyatını bul
          const sellMatch = row.match(/<td[^>]*class="[^"]*selling[^"]*"[^>]*>([\d.,]+)<\/td>/);
          // Değişimi bul
          const changeMatch = row.match(/<td[^>]*class="[^"]*change[^"]*"[^>]*>([-+]?[\d.,]+)<\/td>/);
          // Yüzde değişimi bul
          const changePercentMatch = row.match(/<td[^>]*class="[^"]*percent[^"]*"[^>]*>([-+]?[\d.,]+)%<\/td>/);
          
          if (symbolMatch && nameMatch && buyMatch && sellMatch) {
            const symbol = symbolMatch[1];
            const name = nameMatch[1].trim();
            const buyPrice = parseFloat(buyMatch[1].replace(',', '.'));
            const sellPrice = parseFloat(sellMatch[1].replace(',', '.'));
            const change = changeMatch ? parseFloat(changeMatch[1].replace(',', '.')) : 0;
            const changePercent = changePercentMatch ? parseFloat(changePercentMatch[1].replace(',', '.')) : 0;
            
            currencies.push({
              Code: symbol,
              Name: name,
              BuyPrice: buyPrice,
              SellPrice: sellPrice,
              Change: change,
              ChangePercent: changePercent
            });
          }
        } catch (error) {
          console.error('Error parsing currency row:', error);
        }
      }
    } catch (error) {
      console.error('Error parsing currency HTML:', error);
    }
    
    return currencies;
  }

  private parseGoldHTML(html: string): GoldPrice[] {
    const golds: GoldPrice[] = [];
    
    try {
      // Canlı Döviz altın tablosunu parse et
      const goldRows = html.match(/<tr[^>]*class="[^"]*gold-row[^"]*"[^>]*>[\s\S]*?<\/tr>/g) || [];
      
      for (const row of goldRows) {
        try {
          // Türü bul
          const typeMatch = row.match(/<td[^>]*class="[^"]*type[^"]*"[^>]*>([^<]+)<\/td>/);
          // Fiyatı bul
          const priceMatch = row.match(/<td[^>]*class="[^"]*price[^"]*"[^>]*>([\d.,]+)<\/td>/);
          // Birimi bul
          const unitMatch = row.match(/<td[^>]*class="[^"]*unit[^"]*"[^>]*>([^<]+)<\/td>/);
          // Değişimi bul
          const changeMatch = row.match(/<td[^>]*class="[^"]*change[^"]*"[^>]*>([-+]?[\d.,]+)<\/td>/);
          // Yüzde değişimi bul
          const changePercentMatch = row.match(/<td[^>]*class="[^"]*percent[^"]*"[^>]*>([-+]?[\d.,]+)%<\/td>/);
          
          if (typeMatch && priceMatch && unitMatch) {
            const type = typeMatch[1].trim();
            const price = parseFloat(priceMatch[1].replace(',', '.'));
            const unit = unitMatch[1].trim();
            const change = changeMatch ? parseFloat(changeMatch[1].replace(',', '.')) : 0;
            const changePercent = changePercentMatch ? parseFloat(changePercentMatch[1].replace(',', '.')) : 0;
            
            golds.push({
              Type: type,
              Price: price,
              Unit: unit,
              Change: change,
              ChangePercent: changePercent
            });
          }
        } catch (error) {
          console.error('Error parsing gold row:', error);
        }
      }
    } catch (error) {
      console.error('Error parsing gold HTML:', error);
    }
    
    return golds;
  }
}
